import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import Avatar from "./Avatar";
import { toast } from "../lib/toast";

function FeedRecipeMedia({ recipe }) {
  const media = recipe?.images || [];
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [failedMediaIds, setFailedMediaIds] = useState([]);

  const playableMedia = media.filter((item) => !failedMediaIds.includes(item.id));
  const current =
    playableMedia.find((item) => item.media_type === "video" && item.video) ||
    playableMedia.find((item) => item.image) ||
    null;

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setIsVisible(entries[0]?.isIntersecting ?? false);
      },
      { threshold: 0.6 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!videoRef.current || current?.media_type !== "video" || !current?.video) return;

    if (isVisible) {
      const playPromise = videoRef.current.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isVisible, current]);

  return (
    <div ref={containerRef}>
      {!media.length ? (
        <div className="w-full h-72 bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400">
          No image
        </div>
      ) : current?.media_type === "video" && current?.video ? (
        <video
          ref={videoRef}
          src={current.video}
          className="w-full h-72 object-contain cursor-pointer bg-black"
          muted
          playsInline
          loop
          preload="metadata"
          onError={() => setFailedMediaIds((prev) => [...prev, current.id])}
        />
      ) : (
        <img
          src={current?.image}
          alt={recipe.title}
          className="w-full h-72 object-cover cursor-pointer"
          onError={() => setFailedMediaIds((prev) => [...prev, current.id])}
        />
      )}
    </div>
  );
}

const FeedCard = ({ post }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const recipe = post.recipe;

  // Post-level likes/comments (works for both status and recipe_share)
  const [liked, setLiked] = useState(post.is_liked || false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsNextUrl, setCommentsNextUrl] = useState(null);

  // Recipe-share posts use Recipe comments; status posts use FeedPost comments
  const isRecipeShare = post.post_type === 'recipe_share' && post.recipe;

  const userName = post.user?.first_name && post.user?.last_name
    ? `${post.user.first_name} ${post.user.last_name}`
    : post.user?.first_name || "Unknown";

  const timeAgo = (dateString) => {
    const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  const handleLike = async () => {
    try {
      if (liked) {
        await axiosInstance.delete(`/api/feed/${post.id}/like/`);
        setLiked(false);
        setLikesCount((c) => c - 1);
      } else {
        await axiosInstance.post(`/api/feed/${post.id}/like/`);
        setLiked(true);
        setLikesCount((c) => c + 1);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setLiked(true);
      }
    }
  };

  const [cartLoading, setCartLoading] = useState(false);
  const [cartStatus, setCartStatus] = useState(null); // null | "added" | "exists"

  const handleAddToCart = async () => {
    if (!recipe || cartLoading) return;
    setCartLoading(true);
    try {
      await axiosInstance.post(`/api/cart/recipes/${recipe.id}/`);
      window.dispatchEvent(new Event("cart-changed"));
      setCartStatus("added");
      toast.success("Added to cart.");
    } catch (err) {
      if (err.response?.status === 409) {
        setCartStatus("exists");
        toast.info("Already in cart.");
      } else {
        console.error("Failed to add to cart:", err);
        toast.error("Failed to add recipe to cart.");
      }
    } finally {
      setCartLoading(false);
    }
  };

  // Fetch real comment count for recipe_share posts on mount
  useEffect(() => {
    if (!isRecipeShare) return;
    axiosInstance.get(`/api/recipes/${post.recipe.id}/comments/?page=1`)
      .then((res) => { if (res.data.count != null) setCommentsCount(res.data.count); })
      .catch(() => {});
  }, [isRecipeShare, post.recipe?.id]);

  const fetchComments = async (url) => {
    setLoadingComments(true);
    try {
      const defaultUrl = isRecipeShare
        ? `/api/recipes/${post.recipe.id}/comments/`
        : `/api/feed/${post.id}/comments/`;
      const res = await axiosInstance.get(url || defaultUrl);
      if (url) {
        setComments((prev) => [...prev, ...(res.data.results || [])]);
      } else {
        setComments(res.data.results || []);
        if (isRecipeShare && res.data.count != null) {
          setCommentsCount(res.data.count);
        }
      }
      setCommentsNextUrl(res.data.next);
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleToggleComments = () => {
    if (!showComments) {
      fetchComments();
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      const url = isRecipeShare
        ? `/api/recipes/${post.recipe.id}/comments/`
        : `/api/feed/${post.id}/comments/`;
      const res = await axiosInstance.post(url, { text: commentText });
      setComments((prev) => [res.data, ...prev]);
      setCommentText("");
      setCommentsCount((c) => c + 1);
    } catch (err) {
      console.error("Failed to post comment:", err);
      toast.error("Failed to post comment.");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const url = isRecipeShare
        ? `/api/comments/${commentId}/`
        : `/api/feed/comments/${commentId}/`;
      await axiosInstance.delete(url);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentsCount((c) => c - 1);
    } catch (err) {
      console.error("Failed to delete comment:", err);
      toast.error("Failed to delete comment.");
    }
  };

  const [showAllComments, setShowAllComments] = useState(false);
  const visibleComments = showAllComments ? comments : comments.slice(0, 3);

  const renderComment = (comment) => (
    <div key={comment.id} className="flex items-start space-x-2">
      <div className="flex-shrink-0">
        <Avatar user={comment.user} size="xs" linkToProfile />
      </div>
      <div className="flex-1">
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
          <Link to={`/profile/${comment.user?.id}`} className="font-semibold text-sm text-gray-800 dark:text-white hover:underline">
            {comment.user?.first_name
              ? `${comment.user.first_name} ${comment.user.last_name}`
              : "User"}
          </Link>
          <p className="text-sm text-gray-700 dark:text-gray-300">{comment.text}</p>
        </div>
        <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400">
          <span>{timeAgo(comment.created_at)}</span>
          {currentUser?.id === comment.user?.id && (
            <button
              onClick={() => handleDeleteComment(comment.id)}
              className="text-red-400 hover:text-red-600"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Shared comments section JSX
  const commentsSection = (
    <>
      {/* Engagement counts — likes left, comments right */}
      <div className="px-4 py-2 border-t dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
        <button
          onClick={handleLike}
          className={`hover:underline ${liked ? "text-red-500" : ""}`}
        >
          {likesCount} {likesCount === 1 ? "like" : "likes"}
        </button>
        <button
          onClick={handleToggleComments}
          className="hover:underline"
        >
          {commentsCount} {commentsCount === 1 ? "comment" : "comments"}
        </button>
      </div>

      {/* Action buttons */}
      <div className="px-4 py-2 border-t dark:border-gray-700 flex items-center justify-around">
        <button
          onClick={handleLike}
          className={`flex items-center space-x-1 px-4 py-2 rounded text-sm font-medium transition ${
            liked
              ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          }`}
        >
          <span>{liked ? "Liked" : "Like"}</span>
        </button>

        <button
          onClick={handleToggleComments}
          className="flex items-center space-x-1 px-4 py-2 rounded text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
        >
          <span>Comment</span>
        </button>

        {recipe && (
          <button
            onClick={handleAddToCart}
            disabled={cartLoading || cartStatus === "added" || cartStatus === "exists"}
            className={`flex items-center space-x-1 px-4 py-2 rounded text-sm font-medium transition ${
              cartStatus === "added"
                ? "text-green-600 dark:text-green-400"
                : cartStatus === "exists"
                ? "text-orange-500 dark:text-orange-400"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            } disabled:cursor-not-allowed`}
          >
            <span>
              {cartLoading ? "Adding..." : cartStatus === "added" ? "Added!" : cartStatus === "exists" ? "In Cart" : "Add to Cart"}
            </span>
          </button>
        )}
      </div>

      {/* Comments list */}
      {showComments && (
        <div className="px-4 pb-4 border-t dark:border-gray-700">
          <form onSubmit={handleSubmitComment} className="flex items-center space-x-2 mt-3">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write a comment..."
              maxLength={500}
              className="flex-1 border rounded-full px-4 py-2 text-sm focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={!commentText.trim()}
              className="text-blue-500 font-semibold text-sm disabled:text-gray-300"
            >
              Post
            </button>
          </form>

          <div className="mt-3 space-y-3">
            {loadingComments ? (
              <p className="text-sm text-gray-400">Loading comments...</p>
            ) : comments.length === 0 ? (
              <p className="text-sm text-gray-400">No comments yet.</p>
            ) : (
              <>
                {visibleComments.map(renderComment)}
                {!showAllComments && comments.length > 3 && (
                  <button
                    onClick={() => setShowAllComments(true)}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium"
                  >
                    View {comments.length - 3} more {comments.length - 3 === 1 ? "comment" : "comments"}
                  </button>
                )}
                {showAllComments && commentsNextUrl && (
                  <button
                    onClick={() => fetchComments(commentsNextUrl)}
                    disabled={loadingComments}
                    className="text-sm text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    {loadingComments ? "Loading..." : "Load more comments..."}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );

  // Status post
  if (post.post_type === "status") {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/50 rounded-lg overflow-hidden max-w-2xl mx-auto">
        <div className="px-4 pt-4 pb-3 flex items-start space-x-3">
          <div className="flex-shrink-0">
            <Avatar user={post.user} size="md" linkToProfile />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <Link to={`/profile/${post.user?.id}`} className="font-semibold text-gray-800 dark:text-white text-sm hover:underline">{userName}</Link>
              <span className="text-gray-400 text-xs">{timeAgo(post.created_at)}</span>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mt-1">{post.message}</p>
          </div>
        </div>
        {commentsSection}
      </div>
    );
  }

  // Recipe share post
  return (
    <div className="bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/50 rounded-lg overflow-hidden max-w-2xl mx-auto">
      {/* Author row */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Avatar user={post.user} size="sm" linkToProfile />
          <div>
            <Link to={`/profile/${post.user?.id}`} className="font-semibold text-gray-800 dark:text-white text-sm hover:underline">{userName}</Link>
            <span className="text-gray-400 text-xs ml-2">shared a recipe · {timeAgo(post.created_at)}</span>
          </div>
        </div>
        {recipe?.category && (
          <span className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-600 px-2 py-1 rounded">
            {recipe.category}
          </span>
        )}
      </div>

      {/* Optional message */}
      {post.message && (
        <div className="px-4 pb-2">
          <p className="text-gray-700 dark:text-gray-300 text-sm">{post.message}</p>
        </div>
      )}

      {/* Recipe image */}
      {recipe && (
        <Link to={`/recipes/${recipe.id}`}>
          <FeedRecipeMedia recipe={recipe} />
        </Link>
      )}

      {/* Title + description */}
      {recipe && (
        <div className="px-4 pt-3 pb-2">
          <Link to={`/recipes/${recipe.id}`}>
            <h3 className="text-lg font-bold text-gray-800 dark:text-white hover:text-blue-600">
              {recipe.title}
            </h3>
          </Link>
          {recipe.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{recipe.description}</p>
          )}
          {recipe.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {recipe.tags.slice(0, 5).map((tag) => (
                <span
                  key={tag.id}
                  onClick={() => navigate(`/recipes?view=discover&tag=${tag.name}`)}
                  className="inline-block bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-900/60 transition"
                >
                  #{tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {commentsSection}
    </div>
  );
};

export default FeedCard;
